/**
 * Production Dashboard Service
 * Service for fetching production estimation dashboard data
 */

import { apiClient } from './apiClient';

export interface ProductionDashboardData {
  season: {
    id: number;
    name: string;
    year: number;
  };
  previous_season?: {
    id: number;
    name: string;
    year: number;
  } | null;
  provinces: ProvinceProductionData[];
  totals: ProductionTotals;
  historical_data: HistoricalProductionData[];
}

export interface HistoricalProductionData {
  season_id: number;
  season_name: string;
  year: number;
  planned_surface: number;
  audited_surface: number;
  audits_count: number;
  completed_audits_count: number;
  producer_estimation: number;
  auditor_estimation: number;
  producer_kg_per_ha: number;
  auditor_kg_per_ha: number;
}

export interface ProvinceProductionData {
  province_id: number;
  province_name: string;
  region_name: string;
  country_name: string;
  planned_surface: number;
  audited_surface: number;
  producer_estimation: number;
  auditor_estimation: number;
  growth_percentage: number;
  audits_count: number;
  completed_audits_count: number;
}

export interface ProductionTotals {
  planned_surface: number;
  audited_surface: number;
  producer_estimation: number;
  auditor_estimation: number;
  audits_count: number;
  completed_audits_count: number;
  growth_percentage: number;
  producer_growth_percentage: number;
}

export interface PackerProductionData {
  packer_id: number;
  packer_name: string;
  total_production_kg: number;
  audits_count: number;
  total_surface_ha: number;
}

export interface ProductionTypeData {
  production_type: string;
  production_type_label: string;
  total_surface_ha: number;
  audits_count: number;
}

export interface NetCoverageData {
  net_coverage: string;
  net_coverage_label: string;
  total_surface_ha: number;
  audits_count: number;
}

export const productionDashboardService = {
  /**
   * Get production dashboard data
   * @param seasonId - Season ID (required)
   * @param countryId - Country ID (optional)
   */
  async getDashboardData(
    seasonId: number,
    countryId?: number
  ): Promise<ProductionDashboardData> {
    const params: any = { season_id: seasonId };
    if (countryId) {
      params.country_id = countryId;
    }

    const response = await apiClient.get('/audits/production-dashboard/', {
      params,
    });
    return response.data;
  },

  /**
   * Get production volume by packer
   * @param seasonId - Season ID (required)
   */
  async getPackerProduction(seasonId: number): Promise<{
    season: { id: number; name: string; year: number };
    packers: PackerProductionData[];
  }> {
    const response = await apiClient.get('/audits/packer-production/', {
      params: { season_id: seasonId },
    });
    return response.data;
  },

  /**
   * Get surface by production type
   * @param seasonId - Season ID (required)
   */
  async getProductionTypeSurface(seasonId: number): Promise<{
    season: { id: number; name: string; year: number };
    production_types: ProductionTypeData[];
  }> {
    const response = await apiClient.get('/audits/production-type-surface/', {
      params: { season_id: seasonId },
    });
    return response.data;
  },

  /**
   * Get surface by net coverage
   * @param seasonId - Season ID (required)
   */
  async getNetCoverageSurface(seasonId: number): Promise<{
    season: { id: number; name: string; year: number };
    net_coverage: NetCoverageData[];
  }> {
    const response = await apiClient.get('/audits/net-coverage-surface/', {
      params: { season_id: seasonId },
    });
    return response.data;
  },
};
